import * as React from "react";
import {
  Actions,
  BlockCategory,
  BlockSelection,
  BlockVariant,
  Speaker,
  TextCustomizations,
  Vendor,
} from "src/types";

import Button from "@/components/editor/primitives/button/button-component";
import Image from "@/components/editor/primitives/image/image-component";
import RichText from "@/components/editor/primitives/rich-text";
import Text from "@/components/editor/primitives/text/text-component";
import Video from "@/components/editor/primitives/video/video-component";
import {
  DEFAULT_BRONZE_SPONSORS,
  DEFAULT_GOLDEN_SPONSORS,
  DEFAULT_SCHEDULES,
  DEFAULT_SILVER_SPONSORS,
} from "@/components/editor/shared/constants";
import iframePreview from "@/components/editor/shared/media/iframe/assets/preview.jpg";
import TopLevelIFrame from "@/components/editor/shared/media/iframe/iframe-component";
import VideoGridPreview from "@/components/editor/shared/media/video-grid/assets/preview.png";
import VideoGrid from "@/components/editor/shared/media/video-grid/video-grid-component";
import { VIDEO_AREA_SIZE_SMALL_TYPE } from "@/components/editor/shared/media/video-with-title-settings";
import {
  DEFAULT_NAVIGATION_LINKS,
  getCurrentSelectionLinks,
} from "@/components/editor/shared/navigation";
import embeddedSchedulePreview from "@/components/editor/shared/schedules/embedded-schedule/assets/preview.jpg";
import EmbeddedSchedule from "@/components/editor/shared/schedules/embedded-schedule/embedded-schedule-component";
import Benefits from "@/components/editor/shared/sections/benefits/benefits-component";
import relateEventsPreview from "@/components/editor/shared/sections/related-events/assets/preview.jpg";
import expoPreview from "@/components/editor/themes/crosby/blocks/expo/assets/preview.jpg";
import BoothCard from "@/components/editor/themes/crosby/blocks/expo/booth-card/booth-card-component";
import Booths from "@/components/editor/themes/crosby/blocks/expo/booths-component";
import { DEFAULT_VENDORS } from "@/components/editor/themes/crosby/blocks/expo/constants";
import SyncedBooths from "@/components/editor/themes/crosby/blocks/expo/synced-booths-component";
import headerOption1Preview from "@/components/editor/themes/crosby/blocks/headers/header-option-1/assets/preview.jpg";
import HeaderOption1 from "@/components/editor/themes/crosby/blocks/headers/header-option-1/header-option-1-component";
import headerOption2Preview from "@/components/editor/themes/crosby/blocks/headers/header-option-2/assets/preview.jpg";
import HeaderOption2 from "@/components/editor/themes/crosby/blocks/headers/header-option-2/header-option-2-component";
import headerOption3Preview from "@/components/editor/themes/crosby/blocks/headers/header-option-3/assets/preview.jpg";
import HeaderOption3 from "@/components/editor/themes/crosby/blocks/headers/header-option-3/header-option-3-component";
import headerOption4Preview from "@/components/editor/themes/crosby/blocks/headers/header-option-4/assets/preview.jpg";
import HeaderOption4 from "@/components/editor/themes/crosby/blocks/headers/header-option-4/header-option-4-component";
import standaloneNavigationPreview from "@/components/editor/themes/crosby/blocks/headers/standalone-navigation/assets/preview.jpg";
import StandaloneNavigation from "@/components/editor/themes/crosby/blocks/headers/standalone-navigation/standalone-navigation-component";
import videoWithParagraphPreview from "@/components/editor/themes/crosby/blocks/media/video-with-paragraph/assets/preview.jpg";
import VideoWithParagraph from "@/components/editor/themes/crosby/blocks/media/video-with-paragraph/video-with-paragraph-component";
import videoWithTitlePreview from "@/components/editor/themes/crosby/blocks/media/video-with-title/assets/preview.jpg";
import VideoWithTitle from "@/components/editor/themes/crosby/blocks/media/video-with-title/video-with-title-component";
import schedulePreview from "@/components/editor/themes/crosby/blocks/schedules/assets/preview.jpg";
import DefaultSchedule from "@/components/editor/themes/crosby/blocks/schedules/default-schedule-component";
import SyncedDefaultSchedule from "@/components/editor/themes/crosby/blocks/schedules/synced-default-schedule-component";
import AboutDetails from "@/components/editor/themes/crosby/blocks/sections/about-details/about-details-component";
import aboutDetailsPreview from "@/components/editor/themes/crosby/blocks/sections/about-details/assets/preview.jpg";
import benefitsPreview from "@/components/editor/themes/crosby/blocks/sections/benefits/assets/preview.jpg";
import BenefitCard from "@/components/editor/themes/crosby/blocks/sections/benefits/benefit-card/benefit-card-component";
import { DEFAULT_BENEFIT_CARDS } from "@/components/editor/themes/crosby/blocks/sections/benefits/constants";
import invertedDividerPreview from "@/components/editor/themes/crosby/blocks/sections/divider/assets/inverted-preview.jpg";
import dividerPreview from "@/components/editor/themes/crosby/blocks/sections/divider/assets/preview.jpg";
import {
  DEFAULT_DIVIDER_CTA_TEXT,
  DEFAULT_DIVIDER_DESCRIPTION,
  DEFAULT_DIVIDER_SUBTITLE,
} from "@/components/editor/themes/crosby/blocks/sections/divider/constants";
import Divider from "@/components/editor/themes/crosby/blocks/sections/divider/divider-component";
import dividerPicturePreview from "@/components/editor/themes/crosby/blocks/sections/divider-picture/assets/preview.jpg";
import {
  DEFAULT_DIVIDER_PICTURE_CTA_TEXT,
  DEFAULT_DIVIDER_PICTURE_DESCRIPTION,
  DEFAULT_DIVIDER_PICTURE_SUBTITLE,
} from "@/components/editor/themes/crosby/blocks/sections/divider-picture/constants";
import DividerPicture from "@/components/editor/themes/crosby/blocks/sections/divider-picture/divider-picture-component";
import Gallery from "@/components/editor/themes/crosby/blocks/sections/gallery";
import galleryPreview from "@/components/editor/themes/crosby/blocks/sections/gallery/assets/preview.jpg";
import paragraphPicturePreview from "@/components/editor/themes/crosby/blocks/sections/paragraph-picture/assets/preview.jpg";
import paragraphPictureCTAPreview from "@/components/editor/themes/crosby/blocks/sections/paragraph-picture/assets/previewCTA.jpg";
import ParagraphItem from "@/components/editor/themes/crosby/blocks/sections/paragraph-picture/paragraph-item-component";
import ParagraphPicture from "@/components/editor/themes/crosby/blocks/sections/paragraph-picture/paragraph-picture-component";
import RelatedEventsSection from "@/components/editor/themes/crosby/blocks/sections/related-events";
import richTextPreview from "@/components/editor/themes/crosby/blocks/sections/rich-text-section/assets/preview.jpg";
import RichTextSection from "@/components/editor/themes/crosby/blocks/sections/rich-text-section/rich-text-section-component";
import speakersPreview from "@/components/editor/themes/crosby/blocks/speakers/assets/preview.jpg";
import { DEFAULT_SPEAKERS } from "@/components/editor/themes/crosby/blocks/speakers/constants";
import SpeakerCard from "@/components/editor/themes/crosby/blocks/speakers/speaker-card/speaker-card-component";
import Speakers from "@/components/editor/themes/crosby/blocks/speakers/speakers-component";
import SyncedSpeakers from "@/components/editor/themes/crosby/blocks/speakers/synced-speakers-component";
import sponsorsBronzePreview from "@/components/editor/themes/crosby/blocks/sponsors/preview-bronze.jpg";
import sponsorsGoldenPreview from "@/components/editor/themes/crosby/blocks/sponsors/preview-golden.jpg";
import sponsorsSilverPreview from "@/components/editor/themes/crosby/blocks/sponsors/preview-silver.jpg";
import SponsorCard from "@/components/editor/themes/crosby/blocks/sponsors/sponsor-card/sponsor-card-component";
import Sponsors from "@/components/editor/themes/crosby/blocks/sponsors/sponsors-component";
import SyncedSponsors from "@/components/editor/themes/crosby/blocks/sponsors/synced-sponsors-component";
import { sponsorWidgetList } from "@/components/editor/themes/utils/sponsor-widget-list";
import { IMAGE_UPLOAD_TYPES } from "@/features/analytics";
import humanizeDateRange from "@/utils/dates/humanizeDateRange";
import getInitialItemsOrDefault from "@/utils/get-initial-items-or-default";
import getTopLevelNodeTrees from "@/utils/get-top-level-node-trees";

const selections: Record<BlockCategory, BlockSelection[]> = {
  header: [
    {
      displayName: "selection-menu.selection.blocks.standalone-navigation",
      previewImage: standaloneNavigationPreview,
      Component: ({ query }) => (
        <StandaloneNavigation>
          {getInitialItemsOrDefault(
            getCurrentSelectionLinks(getTopLevelNodeTrees(query)),
            10,
            DEFAULT_NAVIGATION_LINKS
          ).map(({ label, action, noCustomization = true }) => {
            return (
              <Button
                isLink
                key={label}
                text={label}
                action={action}
                noCustomization={noCustomization} //TODO delete, for PAGE-1950, pageBuilderButtonAdvancedStyles flag
              />
            );
          })}
        </StandaloneNavigation>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.header-1",
      previewImage: headerOption1Preview,
      Component: (options) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <HeaderOption1
            // backgroundImage={headerOption1Background.src}
            backgroundImage="/canvas/crosby/images/background.740c04e5.jpg"
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            heading={name}
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-2",
      previewImage: headerOption2Preview,
      Component: (options) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <HeaderOption2
            backgroundImage="/canvas/crosby/images/background.562fab7e.jpg"
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            heading={name || "The Future of Everything"}
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-3",
      previewImage: headerOption3Preview,
      Component: (options) => {
        const {
          event: { name, time_start, time_end, standard_timezone },
        } = options;

        return (
          <HeaderOption3
            backgroundImage="/canvas/crosby/images/background.3a82c474.jpg"
            ctaText={humanizeDateRange(
              time_start,
              time_end,
              standard_timezone
            ).join(" - ")}
            heading={name || "The Future of Everything"}
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
    {
      displayName: "selection-menu.selection.blocks.header-4",
      previewImage: headerOption4Preview,
      Component: () => {
        return (
          <HeaderOption4
            backgroundImage="/canvas/crosby/images/background.151bc68a.jpg"
            isLogoShowing
            logoSize={50}
            imageFit="cover"
          />
        );
      },
    },
  ],
  sections: [
    {
      displayName: "selection-menu.selection.blocks.rich-text",
      previewImage: richTextPreview,
      Component: () => <RichTextSection />,
    },
    {
      displayName: "selection-menu.selection.blocks.related-events",
      previewImage: relateEventsPreview,
      Component: () => <RelatedEventsSection />,
      selectable: ({ flags, eventSeries }) =>
        flags.eventSeries &&
        Array.isArray(eventSeries) &&
        eventSeries.length > 0,
      previewImageContainerStyle: {
        height: "124px",
        backgroundColor: "#F8FAF4",
        paddingTop: "32px",
      },
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture",
      previewImage: paragraphPicturePreview,
      Component: () => (
        <ParagraphPicture>
          <Image src="/canvas/crosby/images/paragraph-picture-placeholder.c8ff9533.jpg" />
          <ParagraphItem />
        </ParagraphPicture>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.paragraph-picture-cta",
      previewImage: paragraphPictureCTAPreview,
      Component: () => (
        <ParagraphPicture>
          <Image src="/canvas/crosby/images/paragraph-picture-placeholder.c8ff9533.jpg" />
          <ParagraphItem showCTA />
        </ParagraphPicture>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.divider-border",
      previewImage: dividerPreview,
      Component: () => (
        <Divider>
          <RichText
            text={`<h3 style='text-align: center;'>${DEFAULT_DIVIDER_SUBTITLE}</h3>`}
          />
          <RichText
            text={`<p style='text-align: center;'>${DEFAULT_DIVIDER_PICTURE_DESCRIPTION}</p>`}
          />
          <Button
            text={DEFAULT_DIVIDER_CTA_TEXT}
            action={{
              kind: Actions.TicketSelector,
            }}
          />
        </Divider>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.divider-solid",
      previewImage: invertedDividerPreview,
      Component: () => (
        <Divider inverted>
          <RichText
            text={`<h3 style='text-align: center;'>${DEFAULT_DIVIDER_SUBTITLE}</h3>`}
          />
          <RichText
            text={`<p style='text-align: center;'>${DEFAULT_DIVIDER_DESCRIPTION}</p>`}
          />
          <Button
            text={DEFAULT_DIVIDER_CTA_TEXT}
            action={{
              kind: Actions.TicketSelector,
            }}
          />
        </Divider>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.divider-picture",
      previewImage: dividerPicturePreview,
      Component: () => (
        <DividerPicture>
          <Image
            src="/canvas/crosby/images/divider-picture-placeholder.39332ac7.jpg"
            blockType={IMAGE_UPLOAD_TYPES.DIVIDER_AND_PICTURE}
          />
          <RichText
            text={`<h3 style='text-align: center;'>${DEFAULT_DIVIDER_PICTURE_SUBTITLE}</h3>`}
          />
          <RichText
            text={`<p style='text-align: center;'>${DEFAULT_DIVIDER_PICTURE_DESCRIPTION}</p>`}
          />
          <Button
            text={DEFAULT_DIVIDER_PICTURE_CTA_TEXT}
            action={{
              kind: Actions.TicketSelector,
            }}
          />
        </DividerPicture>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.about-details",
      previewImage: aboutDetailsPreview,
      Component: () => <AboutDetails />,
    },
    {
      displayName: "selection-menu.selection.blocks.benefits",
      previewImage: benefitsPreview,
      Component: () => (
        <Benefits>
          {DEFAULT_BENEFIT_CARDS.map((item, i) => (
            <BenefitCard key={i}>
              <Image src={item.icon} />
              <Text text={item.title} pattern="headingThree" />
              <Text text={item.description} pattern="description" />
            </BenefitCard>
          ))}
        </Benefits>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.gallery",
      previewImage: galleryPreview,
      Component: () => (
        <Gallery>
          <Image src="/canvas/shared/images/sample-1.971c7ea0.jpg" />
          <Image src="/canvas/shared/images/sample-2.f08a4422.jpg" />
          <Image src="/canvas/shared/images/sample-3.4061fb7d.jpg" />
          <Image src="/canvas/shared/images/sample-4.7365ade5.jpg" />
          <Image src="/canvas/shared/images/sample-5.32bfda46.jpg" />
        </Gallery>
      ),
    },
  ],
  speakers: [
    {
      displayName: "selection-menu.selection.blocks.synced-speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedSpeakers />,
    },
    {
      displayName: "selection-menu.selection.blocks.speakers-grid",
      previewImage: speakersPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Speakers>
          {getInitialItemsOrDefault<Speaker>(
            options.speakers,
            8,
            DEFAULT_SPEAKERS
          ).map((speaker) => (
            <SpeakerCard
              key={speaker.id + speaker.name}
              speaker={speaker}
              isBioAvailable={!!speaker.bio}
            />
          ))}
        </Speakers>
      ),
    },
  ],
  schedule: [
    {
      displayName: "selection-menu.selection.blocks.synced-schedule-highlights",
      previewImage: schedulePreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedDefaultSchedule />,
    },
    {
      displayName: "selection-menu.selection.blocks.schedule-highlights",
      previewImage: schedulePreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <DefaultSchedule
          schedules={
            options.schedules.length > 0 ? options.schedules : DEFAULT_SCHEDULES
          }
        />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.embedded-schedule",
      previewImage: embeddedSchedulePreview,
      Component: (options) => <EmbeddedSchedule slug={options.event.slug} />,
      deprecated: true,
      selectable: () => false,
    },
  ],
  sponsors: [
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.gold || "Golden",
      }),
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Golden Sponsors" layout="golden" />
      ),
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.silver || "Silver",
      }),
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Silver Sponsors" layout="silver" />
      ),
    },
    {
      displayName:
        "selection-menu.selection.blocks.synced-sponsors-custom-tier",
      stringMap: (tc: TextCustomizations) => ({
        tier: tc?.custom_text?.sponsors?.bronze || "Bronze",
      }),
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED,
      Component: () => (
        <SyncedSponsors title="Bronze Sponsors" layout="bronze" />
      ),
    },
    ...sponsorWidgetList(SyncedSponsors),
    {
      displayName: "selection-menu.selection.blocks.synced-sponsors-no-tier",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.SYNCED_EXT,
      Component: () => <SyncedSponsors title="Sponsors" layout="no_tier" />,
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-golden",
      previewImage: sponsorsGoldenPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors title="Golden Sponsors" layout="golden">
          {getInitialItemsOrDefault(
            options.sponsors,
            3,
            DEFAULT_GOLDEN_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-silver",
      previewImage: sponsorsSilverPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors title="Silver Sponsors" layout="silver">
          {getInitialItemsOrDefault(
            options.sponsors,
            4,
            DEFAULT_SILVER_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.sponsors-bronze",
      previewImage: sponsorsBronzePreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Sponsors title="Bronze Sponsors" layout="bronze">
          {getInitialItemsOrDefault(
            options.sponsors,
            6,
            DEFAULT_BRONZE_SPONSORS
          ).map((sponsor, idx) => (
            <SponsorCard sponsor={sponsor} key={idx} />
          ))}
        </Sponsors>
      ),
    },
  ],
  expo: [
    {
      displayName: "selection-menu.selection.blocks.synced-booths-grid",
      previewImage: expoPreview,
      variant: BlockVariant.SYNCED,
      Component: () => <SyncedBooths />,
    },
    {
      displayName: "selection-menu.selection.blocks.booths-grid",
      previewImage: expoPreview,
      variant: BlockVariant.MANUAL,
      Component: (options) => (
        <Booths>
          {getInitialItemsOrDefault<Vendor>(
            options.vendors,
            6,
            DEFAULT_VENDORS
          ).map((vendor) => (
            <BoothCard key={vendor.id} vendor={vendor} />
          ))}
        </Booths>
      ),
    },
  ],
  media: [
    {
      displayName: "selection-menu.selection.blocks.video-paragraph",
      previewImage: videoWithParagraphPreview,
      Component: () => (
        <VideoWithParagraph>
          <ParagraphItem showCTA />
          <Video url="https://youtu.be/9m4oFnBLTos" />
        </VideoWithParagraph>
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.video-title",
      previewImage: videoWithTitlePreview,
      Component: () => (
        <VideoWithTitle videoAreaSize={VIDEO_AREA_SIZE_SMALL_TYPE} />
      ),
    },
    {
      displayName: "selection-menu.selection.blocks.video-grid",
      previewImage: VideoGridPreview,
      Component: () => <VideoGrid />,
    },
    {
      displayName: "selection-menu.selection.blocks.iframe",
      previewImage: iframePreview,
      Component: () => <TopLevelIFrame />,
    },
  ],
};

export default selections;
